import api from '@/libs/axios';
import Cookie from 'js-cookie';
import axios from 'axios';
import advancedPrint from './advanced-print/advancedPrint';
import reversal from './reversal/reversal';

const persistToken = token => {
  if (!token) return;
  Cookie.set('oliveiraToken', token, { expires: 7 });
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('oliveiraToken', token);
  }
};

const resolveRandomizeFlag = value => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase();
    if (trimmed === '') return null;
    if (trimmed === 'true') return 1;
    if (trimmed === 'false') return 0;
    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) {
      return numeric === 0 ? 0 : 1;
    }
    return 1;
  }
  return value ? 1 : 0;
};
const STORE_NICK_FALLBACK = process.env.VUE_APP_STORE_NICK || 'psiqueasy';

const resolveStoreNick = (payload = {}) => {
  const candidates = [
    payload.storeNick,
    payload.store_nick,
    payload.storeNickname,
    payload.store_nickname,
  ];
  const valid = candidates.find(value => typeof value === 'string' && value.trim() !== '');
  return valid ? valid.trim() : STORE_NICK_FALLBACK;
};

const resolveCancelToken = value => {
  if (!value) return undefined;
  if (value.token) return value.token;
  return value;
};
const TERMS_OF_USE_ID = process.env.VUE_APP_API_TERMS_OF_USE_ID;
const LEGACY_TERMS_OF_USE_ENDPOINT = `/terms/${TERMS_OF_USE_ID}`;
const DEFAULT_TERMS_OF_USE_ENDPOINT = process.env.VUE_APP_API_TERMS_V2_ENDPOINT
    || process.env.VUE_APP_API_TERMS_OF_USE_ENDPOINT
    || `/terms-v2/${TERMS_OF_USE_ID}`;
const DEFAULT_PUBLIC_TERMS_OF_USE_ENDPOINT = '/terms-v2/1';

const resolveTermsOfUseEndpoint = (payload = {}) => {
  if (typeof payload === 'string' && payload.trim() !== '') {
    return payload.trim();
  }
  if (payload && typeof payload.endpoint === 'string' && payload.endpoint.trim() !== '') {
    return payload.endpoint.trim();
  }
  return DEFAULT_TERMS_OF_USE_ENDPOINT;
};

const resolveTermsOfUseLegacyEndpoint = (payload = {}) => {
  if (payload && typeof payload.legacyEndpoint === 'string' && payload.legacyEndpoint.trim() !== '') {
    return payload.legacyEndpoint.trim();
  }
  return LEGACY_TERMS_OF_USE_ENDPOINT;
};

const resolveTermsOfUsePublicFallbackEndpoint = (payload = {}) => {
  if (payload && typeof payload.fallbackEndpoint === 'string' && payload.fallbackEndpoint.trim() !== '') {
    return payload.fallbackEndpoint.trim();
  }
  if (TERMS_OF_USE_ID && String(TERMS_OF_USE_ID) === '1') {
    return null;
  }
  return DEFAULT_PUBLIC_TERMS_OF_USE_ENDPOINT;
};

const hasInvalidTermIdMessage = (message = '') => {
  const normalizedMessage = String(message).toLowerCase();
  return normalizedMessage.includes('term id')
        || normalizedMessage.includes('termo informado')
        || normalizedMessage.includes('selecionado e invalido')
        || normalizedMessage.includes('selecionado é inválido');
};

const isInvalidTermsOfUseTermIdError = error => {
  const message = error
        && error.response
        && error.response.data
        && error.response.data.message
    ? error.response.data.message
    : error;
  const termIdErrors = error
        && error.response
        && error.response.data
        && error.response.data.errors
        && Array.isArray(error.response.data.errors.termId)
    ? error.response.data.errors.termId
    : [];

  return termIdErrors.length > 0 || hasInvalidTermIdMessage(message);
};

const resolveTermFromPayload = (basePayload, dataNode) => {
  if (basePayload.term && typeof basePayload.term === 'object') return basePayload.term;
  if (dataNode.term && typeof dataNode.term === 'object') return dataNode.term;

  if (basePayload.id && basePayload.title) {
    return {
      id: basePayload.id,
      title: basePayload.title,
      description: basePayload.description || null,
      create_user_id: basePayload.create_user_id,
      created_at: basePayload.created_at,
      updated_at: basePayload.updated_at,
    };
  }

  return null;
};

const normalizeTermsOfUseResponse = (payload = {}) => {
  const basePayload = payload && typeof payload === 'object' ? payload : {};
  const dataNode = basePayload.data && typeof basePayload.data === 'object' ? basePayload.data : {};
  const latestVersionNode = (basePayload.latestVersion && typeof basePayload.latestVersion === 'object'
    ? basePayload.latestVersion
    : null)
        || (basePayload.latest_version && typeof basePayload.latest_version === 'object' ? basePayload.latest_version : null)
        || (dataNode.latestVersion && typeof dataNode.latestVersion === 'object' ? dataNode.latestVersion : null)
        || (dataNode.latest_version && typeof dataNode.latest_version === 'object' ? dataNode.latest_version : null)
        || (basePayload.version && typeof basePayload.version === 'object' ? basePayload.version : null)
        || (dataNode.version && typeof dataNode.version === 'object' ? dataNode.version : null)
        || {};
  const termsNode = (basePayload.terms && typeof basePayload.terms === 'object'
    ? basePayload.terms
    : null)
        || (dataNode.terms && typeof dataNode.terms === 'object' ? dataNode.terms : null)
        || {};
  const termNode = resolveTermFromPayload(basePayload, dataNode);

  const contentCandidates = [
    latestVersionNode.content,
    termsNode.content,
    basePayload.content,
    dataNode.content,
    latestVersionNode.description,
    termsNode.text,
    basePayload.text,
    dataNode.text,
    termsNode.description,
    basePayload.description,
    dataNode.description,
  ];
  const content = contentCandidates.find(item => typeof item === 'string') || '';

  return {
    ...basePayload,
    ...(termNode ? { term: termNode } : {}),
    latestVersion: latestVersionNode,
    version: latestVersionNode,
    terms: {
      ...termsNode,
      id: termsNode.id || latestVersionNode.id || null,
      term_id: termsNode.term_id || latestVersionNode.term_id || termNode?.id || null,
      version: termsNode.version || latestVersionNode.version || null,
      content,
    },
  };
};

const customer = {
  modules: {
    advancedPrint,
    reversal,
  },
  state: {
    currentProduct: {
      id: null,
      name: null,
    },
    ticketNotificationMonitorVersion: 0,
    ticketNotificationMonitorContext: null,
  },
  getters: {
    currentProduct: state => state.currentProduct,
    ticketNotificationMonitorVersion: state => state.ticketNotificationMonitorVersion,
    ticketNotificationMonitorContext: state => state.ticketNotificationMonitorContext,
  },
  actions: {
    // rota antiga -> filtro e busca
    userEbooksList: ({ commit }, searchQuery) => new Promise(async (resolve, reject) => {
      let newParams = '';
      if (searchQuery != '') {
        newParams = `?search=${searchQuery}`;
      }
      await api.get(`/customer/shopping/list${newParams}`).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data.message);
      });
    }),
    // rota nova -> retorna tudo
    userEbooksListAll: ({ commit }, searchQuery) => new Promise(async (resolve, reject) => {
      let newParams = '';
      if (searchQuery != '') {
        newParams = `?search=${searchQuery}`;
      }
      await api.get(`/customer/shopping/list/all${newParams}`).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data.message);
      });
    }),
    // #
    userColletionDetailShop: ({ commit }, params) =>
    // console.log(params);
      new Promise(async (resolve, reject) => {
        await api.get(`/customer/shopping/purchases/${params.purchaseId}/collection/${params.colletionId}`).then(response => {
          resolve(response.data);
        }).catch(error => {
          reject(error.response.data.message);
        });
      }),
    userComboDetailShop: ({ commit }, params) => new Promise(async (resolve, reject) => {
      await api.get(`/customer/shopping/purchases/${params.purchaseId}/combos/${params.comboId}`).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data.message);
      });
    }),
    listStoreOffersForUser: ({ commit }, searchQuery) => new Promise(async (resolve, reject) => {
      let newParams = '';
      if (typeof searchQuery === 'object') {
        if (searchQuery.collection_id) {
          newParams = `?collection_id=${searchQuery.collection_id}`;
        }
        if (searchQuery.ebook_id) {
          newParams = `?ebook_id=${searchQuery.ebook_id}`;
        }
        if (searchQuery.combo_id) {
          newParams = `?combo_id=${searchQuery.combo_id}`;
        }
      } else if (searchQuery != '') {
        newParams = `?search=${searchQuery}`;
      }
      await api.get(`/customer/stores/psiqueasy/offers${newParams}`).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data.message);
      });
    }),
    // ofertas filtradas
    listStoreOffersEbooksAll: ({ commit }, params) => new Promise(async (resolve, reject) => {
      const payload = (params && typeof params === 'object') ? params : { searchQuery: params };
      const queryParts = [];

      const hasSearchQuery = payload.searchQuery !== undefined && payload.searchQuery !== null && payload.searchQuery !== '';
      if (hasSearchQuery) {
        queryParts.push(`search=${encodeURIComponent(payload.searchQuery)}`);
      }
      const parsedCategory = Number(payload.categorieFilter);
      if (!Number.isNaN(parsedCategory) && parsedCategory !== 0) {
        queryParts.push(`categorieId=${parsedCategory}`);
      }
      if (payload.sortBy) {
        queryParts.push(`sortBy=${encodeURIComponent(payload.sortBy)}`);
      }
      if (payload.sortDirection) {
        queryParts.push(`sortDirection=${encodeURIComponent(payload.sortDirection)}`);
      }
      const randomizeFlag = resolveRandomizeFlag(payload.randomize);
      if (randomizeFlag !== null) {
        queryParts.push(`randomize=${randomizeFlag}`);
      }

      const queryString = queryParts.length ? `&${queryParts.join('&')}` : '';
      const cancelToken = payload.cancelToken ? payload.cancelToken.token : undefined;
      const page = Number(payload.currentPage) || 1;
      const perPage = Number(payload.perPage) || 12;
      await api.get(`/customer/stores/psiqueasy/offers/filterAll?page=${page}&limit=${perPage}${queryString}`, { cancelToken }).then(response => {
        resolve(response.data);
      }).catch(error => {
        if (axios.isCancel(error)) {
          reject(error);
        } else {
          reject(error.response?.data?.message || error.message);
        }
      });
    }),
    listStoreOffersBalancedFilter: ({ commit }, params) => new Promise(async (resolve, reject) => {
      const payload = (params && typeof params === 'object') ? params : { searchQuery: params };
      const queryParts = [];

      const hasSearchQuery = payload.searchQuery !== undefined && payload.searchQuery !== null && payload.searchQuery !== '';
      if (hasSearchQuery) {
        queryParts.push(`search=${encodeURIComponent(payload.searchQuery)}`);
      }
      const parsedCategory = Number(payload.categorieFilter);
      if (!Number.isNaN(parsedCategory) && parsedCategory !== 0) {
        queryParts.push(`categorieId=${parsedCategory}`);
      }
      if (payload.sortBy) {
        queryParts.push(`sortBy=${encodeURIComponent(payload.sortBy)}`);
      }
      if (payload.sortDirection) {
        queryParts.push(`sortDirection=${encodeURIComponent(payload.sortDirection)}`);
      }
      const randomizeFlag = resolveRandomizeFlag(payload.randomize);
      if (randomizeFlag !== null) {
        queryParts.push(`randomize=${randomizeFlag}`);
      }

      const queryString = queryParts.length ? `&${queryParts.join('&')}` : '';
      const cancelToken = resolveCancelToken(payload.cancelToken);
      const page = Number(payload.currentPage) || 1;
      const perPage = Number(payload.perPage) || 12;
      const storeNick = encodeURIComponent(resolveStoreNick(payload));
      await api.get(`/customer/stores/${storeNick}/offers/filterAll-balanced?page=${page}&limit=${perPage}${queryString}`, { cancelToken }).then(response => {
        resolve(response.data);
      }).catch(error => {
        if (axios.isCancel(error)) {
          reject(error);
        } else {
          reject(error.response?.data?.message || error.message);
        }
      });
    }),
    // todas as ofertas
    listStoreOffersAll: ({ commit }, params) => new Promise(async (resolve, reject) => {
      const payload = (params && typeof params === 'object') ? params : { searchQuery: params };
      const queryParts = [];

      const hasSearchQuery = payload.searchQuery !== undefined && payload.searchQuery !== null && payload.searchQuery !== '';
      if (hasSearchQuery) {
        queryParts.push(`search=${encodeURIComponent(payload.searchQuery)}`);
      }
      const parsedCategory = Number(payload.categorieFilter);
      if (!Number.isNaN(parsedCategory) && parsedCategory !== 0) {
        queryParts.push(`categorieId=${parsedCategory}`);
      }
      if (payload.sortBy) {
        queryParts.push(`sortBy=${encodeURIComponent(payload.sortBy)}`);
      }
      if (payload.sortDirection) {
        queryParts.push(`sortDirection=${encodeURIComponent(payload.sortDirection)}`);
      }
      const randomizeFlag = resolveRandomizeFlag(payload.randomize);
      if (randomizeFlag !== null) {
        queryParts.push(`randomize=${randomizeFlag}`);
      }

      const queryString = queryParts.length ? `&${queryParts.join('&')}` : '';
      const cancelToken = payload.cancelToken ? payload.cancelToken.token : undefined;
      const page = Number(payload.currentPage) || 1;
      const perPage = Number(payload.perPage) || 12;
      await api.get(`/customer/stores/psiqueasy/offers/all?page=${page}&limit=${perPage}${queryString}`, { cancelToken }).then(response => {
        resolve(response.data);
      }).catch(error => {
        if (axios.isCancel(error)) {
          reject(error);
        } else {
          reject(error.response?.data?.message || error.message);
        }
      });
    }),
    listStoreOffersBalancedAll: ({ commit }, params) => new Promise(async (resolve, reject) => {
      const payload = (params && typeof params === 'object') ? params : { searchQuery: params };
      const queryParts = [];

      const hasSearchQuery = payload.searchQuery !== undefined && payload.searchQuery !== null && payload.searchQuery !== '';
      if (hasSearchQuery) {
        queryParts.push(`search=${encodeURIComponent(payload.searchQuery)}`);
      }
      const parsedCategory = Number(payload.categorieFilter);
      if (!Number.isNaN(parsedCategory) && parsedCategory !== 0) {
        queryParts.push(`categorieId=${parsedCategory}`);
      }
      if (payload.sortBy) {
        queryParts.push(`sortBy=${encodeURIComponent(payload.sortBy)}`);
      }
      if (payload.sortDirection) {
        queryParts.push(`sortDirection=${encodeURIComponent(payload.sortDirection)}`);
      }
      const randomizeFlag = resolveRandomizeFlag(payload.randomize);
      if (randomizeFlag !== null) {
        queryParts.push(`randomize=${randomizeFlag}`);
      }

      const queryString = queryParts.length ? `&${queryParts.join('&')}` : '';
      const cancelToken = resolveCancelToken(payload.cancelToken);
      const page = Number(payload.currentPage) || 1;
      const perPage = Number(payload.perPage) || 12;
      const storeNick = encodeURIComponent(resolveStoreNick(payload));
      await api.get(`/customer/stores/${storeNick}/offers/all-balanced?page=${page}&limit=${perPage}${queryString}`, { cancelToken }).then(response => {
        resolve(response.data);
      }).catch(error => {
        if (axios.isCancel(error)) {
          reject(error);
        } else {
          reject(error.response?.data?.message || error.message);
        }
      });
    }),
    listStoreOpenOffersByType: ({ commit }, params) => new Promise(async (resolve, reject) => {
      const payload = (params && typeof params === 'object') ? params : {};
      const typeRaw = String(payload.offerType || payload.type || '').trim().toLowerCase();
      const endpointByType = {
        ebook: 'ebooks',
        collection: 'collections',
        combo: 'combos',
      };
      const endpointType = endpointByType[typeRaw];
      if (!endpointType) {
        reject('Tipo de oferta inválido.');
        return;
      }

      const storeNick = encodeURIComponent(resolveStoreNick(payload));
      const page = Math.max(1, Number(payload.currentPage) || 1);
      const perPageRaw = Number(payload.perPage) || 12;
      const perPage = Math.min(Math.max(perPageRaw, 1), 100);
      const queryParts = [`page=${page}`, `limit=${perPage}`];
      const hasSearchQuery = payload.searchQuery !== undefined && payload.searchQuery !== null && payload.searchQuery !== '';
      if (hasSearchQuery) {
        queryParts.push(`search=${encodeURIComponent(payload.searchQuery)}`);
      }

      const cancelToken = resolveCancelToken(payload.cancelToken);
      await api.get(`/customer/stores/${storeNick}/open/${endpointType}?${queryParts.join('&')}`, { cancelToken }).then(response => {
        resolve(response.data);
      }).catch(error => {
        if (axios.isCancel(error)) {
          reject(error);
        } else {
          reject(error.response?.data?.message || error.message);
        }
      });
    }),
    listStorePromotionsEbooksAll: ({ commit }, params) => new Promise(async (resolve, reject) => {
      const payload = (params && typeof params === 'object') ? params : { searchQuery: params };
      const queryParts = [];

      const hasSearchQuery = payload.searchQuery !== undefined && payload.searchQuery !== null && payload.searchQuery !== '';
      if (hasSearchQuery) {
        queryParts.push(`search=${encodeURIComponent(payload.searchQuery)}`);
      }
      const parsedCategory = Number(payload.categorieFilter);
      if (!Number.isNaN(parsedCategory) && parsedCategory !== 0) {
        queryParts.push(`categorieId=${parsedCategory}`);
      }
      if (payload.sortBy) {
        queryParts.push(`sortBy=${encodeURIComponent(payload.sortBy)}`);
      }
      if (payload.sortDirection) {
        queryParts.push(`sortDirection=${encodeURIComponent(payload.sortDirection)}`);
      }
      const randomizeFlag = resolveRandomizeFlag(payload.randomize);
      if (randomizeFlag !== null) {
        queryParts.push(`randomize=${randomizeFlag}`);
      }

      const queryString = queryParts.length ? `&${queryParts.join('&')}` : '';
      const page = Number(payload.currentPage) || 1;
      const perPage = Number(payload.perPage) || 12;
      await api.get(`/customer/stores/psiqueasy/promotions/filter?page=${page}&limit=${perPage}${queryString}`).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data.message);
      });
    }),
    listNextReleases: ({ commit }, searchQuery) => new Promise(async (resolve, reject) => {
      let newParams = '';
      if (searchQuery != '') {
        newParams = `?search=${searchQuery}`;
      }
      await api.get(`/customer/stores/psiqueasy/releases${newParams}`).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data.message);
      });
    }),
    listUpcomingReleases: ({ commit }, params) => new Promise(async (resolve, reject) => {
      await api.get(`/customer/stores/psiqueasy/upcoming/releases/filter?page=${params.page}&limit=${params.limit}&sortBy=${params.sortBy}&sortDirection=${params.sortDirection}`).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data.message);
      });
    }),
    verifyLinkFirstAccess: ({ commit }, linkValidate) => new Promise(async (resolve, reject) => {
      await api.get(`/customer${linkValidate}`).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data.message);
      });
    }),
    termsOfUse: ({ commit }, payload = {}) => new Promise(async (resolve, reject) => {
      const endpoint = resolveTermsOfUseEndpoint(payload);
      const legacyEndpoint = resolveTermsOfUseLegacyEndpoint(payload);
      const publicFallbackEndpoint = resolveTermsOfUsePublicFallbackEndpoint(payload);

      const loadTermsEndpoint = async targetEndpoint => {
        console.log('[termsOfUse] carregando endpoint', targetEndpoint);
        const response = await api.get(targetEndpoint);
        return normalizeTermsOfUseResponse(response.data);
      };

      await loadTermsEndpoint(endpoint).then(normalizedResponse => {
        resolve(normalizedResponse);
      }).catch(async error => {
        const status = error && error.response ? error.response.status : null;
        const invalidTermIdError = isInvalidTermsOfUseTermIdError(error);
        const canFallbackToPublicTerm = publicFallbackEndpoint
                        && publicFallbackEndpoint !== endpoint
                        && (invalidTermIdError || [404, 405, 500].includes(status));
        const canFallbackToLegacy = legacyEndpoint
                        && legacyEndpoint !== endpoint
                        && [404, 405, 500].includes(status);

        if (canFallbackToPublicTerm) {
          await loadTermsEndpoint(publicFallbackEndpoint).then(fallbackResponse => {
            resolve(fallbackResponse);
          }).catch(async publicFallbackError => {
            if (canFallbackToLegacy) {
              await loadTermsEndpoint(legacyEndpoint).then(legacyResponse => {
                resolve(legacyResponse);
              }).catch(legacyError => {
                const message = legacyError
                                        && legacyError.response
                                        && legacyError.response.data
                                        && legacyError.response.data.message
                  ? legacyError.response.data.message
                  : 'Não foi possível carregar os termos de uso.';
                reject(message);
              });
              return;
            }

            const message = publicFallbackError
                                && publicFallbackError.response
                                && publicFallbackError.response.data
                                && publicFallbackError.response.data.message
              ? publicFallbackError.response.data.message
              : 'Não foi possível carregar os termos de uso.';
            reject(message);
          });
          return;
        }

        if (canFallbackToLegacy) {
          await loadTermsEndpoint(legacyEndpoint).then(legacyResponse => {
            resolve(legacyResponse);
          }).catch(legacyError => {
            const message = legacyError
                                && legacyError.response
                                && legacyError.response.data
                                && legacyError.response.data.message
              ? legacyError.response.data.message
              : 'Não foi possível carregar os termos de uso.';
            reject(message);
          });
          return;
        }

        const message = error
                        && error.response
                        && error.response.data
                        && error.response.data.message
          ? error.response.data.message
          : 'Não foi possível carregar os termos de uso.';
        reject(message);
      });
    }),
    finishFirstAccess: ({ commit }, dataUser) => {
      const payload = { ...dataUser };
      const { linkValidate } = payload;
      delete payload.linkValidate;
      delete payload.passwordConfirm;
      return new Promise(async (resolve, reject) => {
        await api.post(`/customer${linkValidate}`, payload).then(response => {
          persistToken(response.data.token);
          response.data.users.roles.map(e => {
            e.permissions.map(el => {
              response.data.users.roles.push({
                action: 'read',
                subject: el.name,
              });
            });
          });
          response.data.users.roles.push({
            action: 'read',
            subject: 'Public',
          });
          commit('addUser', { dataUser: response.data.users });
          resolve(response);
        }).catch(error => {
          reject(error.response?.data || error);
        });
      });
    },
    pdfViewCustomer: ({ commit }, params) => new Promise(async (resolve, reject) => {
      if (params.type == 'ebook') {
        await api.get(`/customer/purchases/${params.purchase_id}/ebooks/${params.ebook_id}/urlgen?page=${params.page}`).then(response => {
          resolve(response.data);
        }).catch(error => {
          reject(error.response.data);
        });
      } else if (params.type == 'preview') {
        await api.get(`/customer/ebooks/${params.ebook_id}/urlgen/preview?page=${params.page}`).then(response => {
          resolve(response.data);
        }).catch(error => {
          reject(error.response.data);
        });
      }
    }),
    pdfViewCustomerPrint: ({ commit }, params) => new Promise(async (resolve, reject) => {
      let rangePage = '';
      params.rangePage.map((el, idx) => {
        if (idx == 0) {
          rangePage = el.value.toString();
        } else {
          rangePage = rangePage.concat(`,${el.value.toString()}`);
        }
      });
      await api.get(`/customer/purchases/${params.purchase_id}/ebooks/${params.ebook_id}/urlgen/print?rangePage=${rangePage}`).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data);
      });
    }),
    getUserProfileAddress: ({ commit }) => new Promise(async (resolve, reject) => {
      await api.get('/profile/addresses').then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data.message);
      });
    }),
    createUserProfileAddress: ({ commit }, data) => new Promise(async (resolve, reject) => {
      await api.post('/profile/addresses', data).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data.errors);
      });
    }),
    deleteUserProfileAddress: ({ commit }, idAddress) => new Promise(async (resolve, reject) => {
      await api.delete(`/profile/addresses/${idAddress}`).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data?.message || error.response.data);
      });
    }),
    userChangeAddressDefault: ({ commit }, idAddress) => new Promise(async (resolve, reject) => {
      await api.patch(`/profile/addresses/${idAddress}`, {
        default: true,
      }).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data.message);
      });
    }),
    customerRequestsList: ({ commit }, params) => new Promise(async (resolve, reject) => {
      let outhersParams = '';
      if (params.search != '' && params.search != null) {
        outhersParams = `${outhersParams}&search=${params.search}`;
      }
      if (params.format != '' && params.format != null) {
        outhersParams = `${outhersParams}&paymentStatus=${params.paymentStatus}`;
      }
      if (params.category != '' && params.category != null) {
        outhersParams = `${outhersParams}&PlatformId=${params.PlatformId}`;
      }

      if (params.sortby == null) {
        params.sortby = 'created_at';
      }
      if (params.sortdirection == null) {
        params.sortdirection = 'desc';
      }
      if (params.page == null) {
        params.page = '1';
      }
      if (params.limit == null) {
        params.limit = '10';
      }
      if (params.status == null) {
        params.status = 'active';
      }
      await api.get(`/customer/purchases?page=${params.page}&limit=${params.limit}&sortBy=${params.sortby}&sortDirection=${params.sortdirection}${outhersParams}`).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data.message);
      });
    }),
    customerTicketsList: ({ commit }, params) => new Promise(async (resolve, reject) => {
      let outhersParams = '';

      if (params.sortby == null) {
        params.sortby = 'created_at';
      }
      if (params.sortdirection == null) {
        params.sortdirection = 'desc';
      }
      if (params.page == null) {
        params.page = '1';
      }
      if (params.limit == null) {
        params.limit = '10';
      }
      if (params.search) {
        outhersParams += `&search=${params.search}`;
      }
      if (params.state) {
        outhersParams += `&state=${params.state}`;
      }
      if (params.status_id) {
        outhersParams += `&status_id=${params.status_id}`;
      }
      if (params.priority_id) {
        outhersParams += `&priority_id=${params.priority_id}`;
      }
      await api.get(`/customer/tickets?page=${params.page}&limit=${params.limit}&sortBy=${params.sortby}&sortDirection=${params.sortdirection}${outhersParams}`, {
        skipForceLogoutOn401: true,
      }).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data.message);
      });
    }),
    customerTicketDetails: ({ commit }, params) => new Promise(async (resolve, reject) => {
      await api.get(`/customer/tickets/${params.protocolNumber}`).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data.message);
      });
    }),
    customerChangeStatusTicket: ({ commit }, params) => new Promise(async (resolve, reject) => {
      await api.patch(`/customer/tickets/${params.protocolNumber}`, { status: 2 }).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data.message);
      });
    }),
    customerCloseTicket: ({ commit }, params) => new Promise(async (resolve, reject) => {
      try {
        const response = await api.post(`/customer/tickets/${params.protocolNumber}/close`);
        resolve(response.data);
      } catch (error) {
        reject(error.response?.data || error);
      }
    }),
    customerReopenTicket: ({ commit }, params) => new Promise(async (resolve, reject) => {
      try {
        const response = await api.post(`/customer/tickets/${params.protocolNumber}/reopen`);
        resolve(response.data);
      } catch (error) {
        reject(error.response?.data || error);
      }
    }),
    customerTicketComment: ({ commit }, params) => new Promise(async (resolve, reject) => {
      try {
        if (params?.file_image?.length > 0) {
          const formData = new FormData();
          formData.append('comment', params.comment || '');

          for (const key in params.file_image) {
            const attachment = params.file_image[key];

            if (!attachment) {
              continue;
            }

            if (attachment instanceof Blob) {
              formData.append(`file_image[${key}]`, attachment, attachment.name || `comment-image-${key}.png`);
            } else if (attachment?.path) {
              formData.append(`file_image[${key}]`, await (await fetch(attachment.path)).blob(), attachment.name || `comment-image-${key}.png`);
            }
          }

          const response = await api.post(`/customer/tickets/${params.protocolNumber}/comments`, formData);
          resolve(response.data);
          return;
        }

        await api.post(`/customer/tickets/${params.protocolNumber}/comments`, {
          comment: params.comment,
        }).then(response => {
          resolve(response.data);
        }).catch(error => {
          reject(error.response.data);
        });
      } catch (error) {
        reject(error.response?.data || error);
      }
    }),
    customerTicketCommentStar: ({ commit }, params) => new Promise(async (resolve, reject) => {
      await api.post(`/customer/tickets/${params.protocolNumber}/comments/${params.commentId}/stars`, {
        star: params.star,
      }).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data);
      });
    }),
    customerTicketFeedback: ({ commit }, params) => new Promise(async (resolve, reject) => {
      try {
        const protocolNumber = String(params?.protocolNumber ?? '').trim();

        if (protocolNumber === '') {
          const missingTargetError = new Error('Não foi possível identificar o ticket para registrar a avaliação final.');
          missingTargetError.errors = {
            protocolNumber: ['Protocolo do ticket não informado.'],
          };
          reject(missingTargetError);
          return;
        }

        const response = await api.post(
          `/customer/tickets/${encodeURIComponent(protocolNumber)}/feedback`,
          {
            stars: params.stars,
            comment: params.comment,
          },
        );
        resolve(response.data);
      } catch (error) {
        reject(error.response?.data || error);
      }
    }),
    getUniqueRequest: ({ commit }, idSale) => new Promise(async (resolve, reject) => {
      await api.get(`/customer/purchases/${idSale.id}`)
        .then(response => {
          resolve(response.data);
        }).catch(error => {
          reject(error.response.data);
        });
    }),
    getRequestStoreInfo: ({ commit }) => new Promise(async (resolve, reject) => {
      await api.get('/customer/stores/psiqueasy/info')
        .then(response => {
          resolve(response.data);
        }).catch(error => {
          reject(error.response.data);
        });
    }),
    getStoreInfoById: ({ commit }, storeId) => new Promise(async (resolve, reject) => {
      if (storeId === undefined || storeId === null) {
        return resolve(null);
      }

      const identifier = typeof storeId === 'string' ? storeId.trim() : String(storeId).trim();
      const isNumeric = identifier !== '' && !Number.isNaN(Number(identifier));

      if (identifier === '' || identifier.length < 3 || isNumeric) {
        return resolve(null);
      }

      const normalizedId = encodeURIComponent(identifier);
      try {
        const response = await api.get(`/customer/stores/${normalizedId}/info`);
        resolve(response.data);
      } catch (error) {
        reject(error.response?.data || error.message);
      }
    }),
    getClientScreen: ({ commit }, screen) => new Promise(async (resolve, reject) => {
      await api.get(`/profile/screen/scalings?scaling=${screen}`)
        .then(response => {
          resolve(response.data);
        }).catch(error => {
          reject(error.response.data);
        });
    }),
    saveClientScreen: ({ commit }, screen) => new Promise(async (resolve, reject) => {
      await api.post('/profile/screen/scalings', { scaling: screen })
        .then(response => {
          resolve(response.data);
        }).catch(error => {
          reject(error.response.data);
        });
    }),
    downloadEbook: ({ commit }, data) => new Promise(async (resolve, reject) => {
      await api.get(`/customer/purchases/${data.purchase_id}/ebooks/${data.ebook_id ? data.ebook_id : data.id}/down`)
        .then(response => {
          resolve(response.data);
        }).catch(error => {
          reject(error.response.data);
        });
    }),
    downloadPreviewEbook: ({ commit }, data) => new Promise(async (resolve, reject) => {
      await api.get(`/customer/ebooks/${data.id}/urlgen/preview/down`)
        .then(response => {
          resolve(response.data);
        }).catch(error => {
          reject(error.response.data);
        });
    }),
    customerService: ({ commit }) => new Promise(async (resolve, reject) => {
      await api.get('/customer/stores/psiqueasy/contacts')
        .then(response => {
          resolve(response.data);
        }).catch(error => {
          reject(error.response.data);
        });
    }),
    customerServiceOpen: ({ commit }) => new Promise(async (resolve, reject) => {
      await api.get('/stores/psiqueasy/contacts')
        .then(response => {
          resolve(response.data);
        }).catch(error => {
          reject(error.response.data);
        });
    }),
    getBudgetData: ({ commit }, link) => new Promise(async (resolve, reject) => {
      await api.get(`/customer/budget/purchases/${link}`).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data);
      });
    }),
    createTicket: async ({ commit }, params) => new Promise(async (resolve, reject) => {
      try {
        const formData = new FormData();
        formData.append('title', params.title);
        formData.append('reason', params.reason);
        formData.append('phone_number', params.phone_number);
        formData.append('category_id', params.category_id);
        formData.append('priority_id', params.priority_id);

        const attachments = Array.isArray(params?.file_image)
          ? params.file_image.filter(Boolean)
          : [];

        if (attachments.length > 0) {
          const normalizedAttachments = await Promise.all(
            attachments.map(async (attachment, index) => {
              if (attachment instanceof Blob) {
                return {
                  file: attachment,
                  name: attachment.name || `ticket-image-${index}.png`,
                };
              }

              if (attachment?.file instanceof Blob) {
                return {
                  file: attachment.file,
                  name: attachment.file.name || attachment.name || `ticket-image-${index}.png`,
                };
              }

              if (attachment?.path) {
                const attachmentBlob = await fetch(attachment.path).then(response => response.blob());
                return {
                  file: attachmentBlob,
                  name: attachment.name || `ticket-image-${index}.png`,
                };
              }

              return null;
            }),
          );

          normalizedAttachments
            .filter(Boolean)
            .forEach(attachment => {
              formData.append('file_image[]', attachment.file, attachment.name);
            });
        }

        const response = await api.post('/customer/tickets', formData);
        resolve(response.data);
      } catch (error) {
        reject(error.response?.data || error);
      }
    }),
    requestChangeItinOrFullname: async ({ commit }, params) => {
      const formData = new FormData();
      if (params.itin_number) {
        formData.append('itin_number', params.itin_number);
      }
      if (params.fullName) {
        formData.append('fullName', params.fullName);
      }
      if (params?.file_image?.length > 0) {
        for (const key in params.file_image) {
          formData.append('doc_file', await (await fetch(params.file_image[0].path)).blob());
        }
      }
      return new Promise(async (resolve, reject) => {
        await api.post('/profile/change', formData).then(response => {
          // commit("addPurchasesRefunds", response.data);
          resolve(response.data);
        }).catch(error => {
          reject(error.response.data);
        });
      });
    },
    getCustomerCategories: ({ commit }, link) => new Promise(async (resolve, reject) => {
      await api.get('/customer/tickets/categories').then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data);
      });
    }),
    getCustomerPriorities: ({ commit }, link) => new Promise(async (resolve, reject) => {
      await api.get('/customer/tickets/priorities').then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data);
      });
    }),
    getNotifications: ({ commit }, link) => new Promise(async (resolve, reject) => {
      await api.get('/customer/notification', {
        skipForceLogoutOn401: true,
      }).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data);
      });
    }),
    viewNotificationsChange: ({ commit }, params) => new Promise(async (resolve, reject) => {
      await api.patch(`/customer/notification/${params.id}`, {
        status: 'true',
        type: params.not_type,
      }).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error.response.data);
      });
    }),
    triggerTicketNotificationMonitor: ({ commit }, payload = {}) => new Promise(resolve => {
      commit('BUMP_TICKET_NOTIFICATION_MONITOR', payload);
      resolve(payload);
    }),
  },
  mutations: {
    SET_CURRENT_PRODUCT(state, payload) {
      state.currentProduct = payload
        ? {
          id: payload.id ?? null,
          name: payload.name ?? null,
        }
        : { id: null, name: null };
    },
    BUMP_TICKET_NOTIFICATION_MONITOR(state, payload = {}) {
      state.ticketNotificationMonitorVersion += 1;
      state.ticketNotificationMonitorContext = {
        ...payload,
        triggeredAt: Date.now(),
      };
    },
  },
};

export default customer;
